import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { productList, deleteProductApi } from "../../services/Api";

export const fetchProducts = createAsyncThunk(
    'products/fetchProducts',
    async (params = {}, { getState }) => {
        try {
            const { filters } = getState().products;
            const requestParams = {
                sort: 'id',
                sort_order: filters.sorting || 'desc',
                page: filters.page,
                paginate_per_page: filters.paginate_per_page,
                ...(filters.search && { product_name: filters.search }),
                ...(filters.from && { from: filters.from }),
                ...(filters.to && { to: filters.to }),
                ...(filters.status && { status: filters.status }),
                ...(filters.company_id && { company_id: filters.company_id })
            };

            // Merge any additional params passed directly to the thunk
            const mergedParams = { ...requestParams, ...params };

            const response = await productList(mergedParams);
            return response;
        } catch (error) {
            throw error;
        }
    }
);

export const deleteProduct = createAsyncThunk(
    'products/deleteProduct',
    async (deleteData, { rejectWithValue }) => {
        try {
            const response = await deleteProductApi(deleteData);
            return {
                data: response.data,
                productId: deleteData.productId, // Pass productId for local state update
                itemId: deleteData.item_id, // Pass itemId for reference
                skuIds: deleteData.sku_id_list // Pass SKU IDs for reference
            };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete product');
        }
    }
);

const initialState = {
    products: [],
    loading: false,
    error: null,
    shouldRefresh: false,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 10
    },
    filters: {
        from: '',
        to: '',
        search: '',
        status: '',
        sorting: 'desc',
        page: "1",
        paginate_per_page: "10",
        company_id: null
    },
    // Add delete related states
    deleteLoading: false,
    deleteError: null
};

const productsSlice = createSlice({
    name: "products",
    initialState,
    reducers: {
        setShouldRefresh: (state, action) => {
            state.shouldRefresh = action.payload;
        },
        setFilters: (state, action) => {
            // If we're setting a company_id to null, reset all filters
            if (action.payload.hasOwnProperty('company_id') && action.payload.company_id === null) {
                state.filters = { ...initialState.filters };
            } else {
                state.filters = { ...state.filters, ...action.payload };
            }
        },
        resetFilters: (state, action) => {
            // If preserveCompanyId is true, keep the company ID when resetting
            const preserveCompanyId = action.payload?.preserveCompanyId === true;
            const companyId = preserveCompanyId ? state.filters.company_id : null;

            state.filters = {
                ...initialState.filters,
                ...(companyId ? { company_id: companyId } : {})
            };
        },
        // Use this action for a complete reset, including company ID
        resetAllFilters: (state) => {
            state.filters = { ...initialState.filters };
        },
        clearCompanyFilter: (state) => {
            state.filters = { ...initialState.filters };
        },
        // Clear delete error
        clearDeleteError: (state) => {
            state.deleteError = null;
        },
        // Manually remove product from state
        removeProduct: (state, action) => {
            state.products = state.products.filter(
                product => product.id !== action.payload
            );
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Products cases
            .addCase(fetchProducts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload.data || [];
                if (action.payload.meta) {
                    state.pagination = {
                        currentPage: action.payload.meta.current_page || 1,
                        totalPages: action.payload.meta.last_page || 1,
                        totalItems: action.payload.meta.total || 0,
                        perPage: action.payload.meta.per_page || 10
                    };
                }
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.products = [];
            })

            // Delete Product cases
            .addCase(deleteProduct.pending, (state) => {
                state.deleteLoading = true;
                state.deleteError = null;
            })
            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = null;

                // Remove the deleted product from the local state
                if (action.payload.productId) {
                    state.products = state.products.filter(
                        product => product.id !== action.payload.productId
                    );

                    // Update pagination total items
                    state.pagination.totalItems = Math.max(0, state.pagination.totalItems - 1);

                    // Adjust current page if needed
                    if (state.products.length === 0 && state.pagination.currentPage > 1) {
                        state.pagination.currentPage = state.pagination.currentPage - 1;
                        // You might want to trigger a refetch here
                        state.shouldRefresh = true;
                    }
                }
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = action.payload;
            });
    }
});

export const {
    setFilters,
    resetFilters,
    setShouldRefresh,
    clearCompanyFilter,
    resetAllFilters,
    clearDeleteError,
    removeProduct
} = productsSlice.actions;

export default productsSlice.reducer;